import { Injectable } from '@angular/core';
import { BeyondSupplier } from 'src/app/model/beyond-supplier';
import { UserService } from '../user/user.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BeyondSupplierInterface } from 'src/app/model/beyond-supplier-interface';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '../storage/storage.service';
import { AppConstants } from 'src/app/constants/app-constants';

/**
 * BeyondSupplierService is used to hold/move beyond supplier data from the API to the website
 */
@Injectable({
  providedIn: 'root'
})
export class BeyondSupplierService {
  /** A list of beyond suppliers the user has access to */
  private _beyondSuppliers: BeyondSupplier[] = [];
  private _beyondSuppliersSubject = new Subject<BeyondSupplier[]>();
  public beyondSuppliers$ = this._beyondSuppliersSubject.asObservable();

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>("0:00");
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the beyond supplier service, used to initialize services
   * 
   * @param http
   * @param authService 
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
    private _toastrService: ToastrService,
  ) {
    // Get the beyond suppliers when the token first loads
    this._userService.token$.subscribe(
      (token: string) => {
        if (token !== "") this._fetchBeyondSuppliers();
      });

    // Continuously query the backend to receive new beyond suppliers
    this._startTimer(0);

    this.timer.subscribe(
      (x: number) => {
        this._timers.splice(this._timers.indexOf(x));
        this._startTimer(x + 1);
        this._fetchBeyondSuppliers();
      });
  }

  /**
   * Get a list of beyond suppliers
   * 
   * @return A reference to the current beyond suppliers
   */
  public getBeyondSuppliers(): BeyondSupplier[] {
    return this._beyondSuppliers;
  }

  /**
   * Get a beyond supplier by id. Returns undefined if not found
   * 
   * @param id The id of the beyond supplier to get
   * @return The beyond supplier with the given id
   */
  public getBeyondSupplierById(id: number): BeyondSupplier | undefined {
    for (let i = 0; i < this._beyondSuppliers.length; i++) {
      if (this._beyondSuppliers[i].id === id) {
        return this._beyondSuppliers[i];
      }
    }

    return undefined;
  }

  /**
   * Set the beyond suppliers to a given array
   * 
   * @param beyondSuppliers
   */
  private _setBeyondSuppliers(beyondSuppliers: BeyondSupplier[]): void {
    this._beyondSuppliers = beyondSuppliers.map(x => x.copyOf());
    this._beyondSuppliersSubject.next(beyondSuppliers.map(x => x.copyOf()));
  }

  /**
   * Update a beyond supplier
   * 
   * @param id The id of the beyond supplier to update
   * @param sup The beyond supplier to update
   */
  public updateBeyondSupplier(id: number, sup: BeyondSupplier): void {
    sup.id = id; // Update the id of the interface for addition to the list of beyond suppliers later
    const intf = sup.asInterface();

    this._http.put<BeyondSupplierInterface>(environment.baseUrl + `/beyond-suppliers/${id}`, intf).subscribe({
      next: (updatedIntf: BeyondSupplierInterface): void => {
        // Update the subject to have the new beyond supplier on success
        const old = this._beyondSuppliers.map(x => x.copyOf());
        for (let i = 0; i < old.length; i++) {
          if (old[i].id === id) {
            old[i] = new BeyondSupplier(updatedIntf);
            this._setBeyondSuppliers(old);
            this._toastrService.success("Successfully updated beyond supplier!");
            break;
          }
        }
      },
    });
  }

  /**
   * Create a new beyond supplier
   * 
   * @param intf the beyond supplier to create 
   */
  public createBeyondSupplier(intf: BeyondSupplierInterface): void {
    this._http.post(environment.baseUrl + `/beyond-suppliers`, intf).subscribe({
      next: (id: any): void => {
        intf.id = id as number;

        // Update the subject to have the new beyond supplier on success
        const old: BeyondSupplier[] = this._beyondSuppliers;
        old.push(new BeyondSupplier(intf));

        this._setBeyondSuppliers(old);
        this._toastrService.success("Successfully created beyond supplier!");
      },
    });
  }

  /**
   * Suspend a beyond supplier
   * 
   * @param id the id of the beyond supplier to suspend 
   */
  public suspendBeyondSupplier(id: number): void {
    this._http.put(environment.baseUrl + `/beyond-suppliers/${id}/suspend`, {}).subscribe({
      next: (_: any): void => {
        // Update the status of the suspended beyond supplier 
        const old: BeyondSupplier[] = this._beyondSuppliers;
        for (let i = 0; i < old.length; i++) {
          if (old[i].id === id) {
            old[i].status = "suspended";
            this._setBeyondSuppliers(old);
            this._toastrService.success("Successfully suspended beyond supplier!");
            break;
          }
        }
      },
    });
  }

  /**
   * Terminate a beyond supplier
   * 
   * @param id the id of the beyond supplier to terminate 
   */
  public terminateBeyondSupplier(id: number): void {
    this._http.put(environment.baseUrl + `/beyond-suppliers/${id}/terminate`, {}).subscribe({
      next: (_: any): void => {
        // Update the status of the terminated beyond supplier
        const old: BeyondSupplier[] = this._beyondSuppliers;
        for (let i = 0; i < old.length; i++) {
          if (old[i].id === id) {
            old[i].status = "terminated";
            this._setBeyondSuppliers(old);
            this._toastrService.success("Successfully terminated beyond supplier!");
            break;
          }
        }
      },
    });
  }

  /**
   * Get beyond suppliers from the backend. You should subscribe to the
   * 'beyondSuppliers$' field to receive updates
   */
  private _fetchBeyondSuppliers(): void {
    this._http.get(environment.baseUrl + "/beyond-suppliers").subscribe({
      next: (data: Object): void => {
        // Cast the data into a list of beyond supplier interfaces
        const beyondSupplierInterfaces = data as BeyondSupplierInterface[];
        if (beyondSupplierInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into beyondSupplierInterface list`);
        }

        // Convert the interfaces to class values
        const beyondSuppliers: BeyondSupplier[] = [];
        for (let intf of beyondSupplierInterfaces) {
          beyondSuppliers.push(new BeyondSupplier(intf));
        }

        // Propagate changes if different
        const current = this._beyondSuppliers;
        if (!arraysEqual(current, beyondSuppliers)) {
          this._setBeyondSuppliers(beyondSuppliers);
          console.log(`[LOG]: successfully queried beyond suppliers`);
        }
      },
    });
  }

  /**
   * Start the last updated time from 0
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(`${minutes}:${("" + seconds).length === 1 ? "0" : ""}${seconds}`);
    }
  }
}

const arraysEqual = (a: BeyondSupplier[], b: BeyondSupplier[]): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (var i = 0; i < a.length; ++i) {
    if ((a[i] === null && a[i] !== b[i]) || !a[i].equals(b[i])) return false;
  }
  return true;
}

const delay = (delay: number) => {
  return new Promise(r => { setTimeout(r, delay) });
}
